import { useState } from 'react';
import { AddToCalendarButton } from 'add-to-calendar-button-react';

import agent from '../../../../api/agent';
import { Static } from '../../../../static';
import { EtichettaValore } from '../../../../models/etichetta-valore';
import utils from '../../../../common/utils';
import { PeriodoLettura } from '../models/periodo-lettura';

type Props = {
	matricolaContatore: EtichettaValore;
	ultimaLettura: EtichettaValore;
	documentoN: EtichettaValore;
	periodoLettura: PeriodoLettura;
};

type InfoRowProps = {
	iconSrc: string;
	label: string;
	value: string;
	className?: string;
};

const InfoRow = ({ iconSrc, label, value, className = '' }: InfoRowProps) => (
	<div className={`app-info-div ${className}`.trim()}>
		<img alt='' className='app-info-icon' src={iconSrc} />
		<div className='ms-2'>
			<span>{label}</span>
			<br />
			<span className='app-bold'>{value}</span>
		</div>
	</div>
);

type MeterDetailBodyProps = {
	matricolaContatore: EtichettaValore;
	ultimaLettura: EtichettaValore;
	periodoLettura?: PeriodoLettura;
	validPeriodoLettura: boolean;
	periodoLetturaStart: Date;
	periodoLetturaEnd: Date;
	onSOLClick: () => void;
	firstRowClassName?: string;
};

const MeterDetailBody = ({
	matricolaContatore,
	ultimaLettura,
	periodoLettura,
	validPeriodoLettura,
	periodoLetturaStart,
	periodoLetturaEnd,
	onSOLClick,
	firstRowClassName = '',
}: MeterDetailBodyProps) => (
	<>
		<InfoRow
			iconSrc='/images/veritas/invoice/CounterIcon.svg'
			label={matricolaContatore.Etichetta}
			value={matricolaContatore.Valore}
			className={firstRowClassName}
		/>

		<InfoRow
			iconSrc='/images/veritas/invoice/LastReadingIcon.svg'
			label={ultimaLettura.Etichetta}
			value={`${ultimaLettura.Valore} mc`}
			className='mt-4'
		/>

		{periodoLettura && (
			<InfoRow
				iconSrc='/images/veritas/invoice/CalendarIcon.svg'
				label={periodoLettura.Etichetta}
				value={periodoLettura.Valore}
				className='mt-4'
			/>
		)}

		<div className='app-info-div my-4'>
			<img
				alt=''
				className='app-info-icon'
				src='/images/veritas/invoice/WorldIcon.svg'
			/>
			<div className='ms-2'>
				<p className='mb-0'>
					Puoi comunicare la lettura accedendo sullo{' '}
					<a
						className='app-link'
						href={Static.urlVeritasSOL}
						target='_blank'
						rel='noopener noreferrer'
						onClick={onSOLClick}
					>
						Sportello Online Veritas
					</a>
				</p>
			</div>
		</div>

		{validPeriodoLettura && (
			<AddToCalendarButton
				language='it'
				customCss='/atcb.css'
				buttonStyle='custom'
				label='Ricordamelo'
				name={`Effettua autolettura dell’acqua contatore matricola ${matricolaContatore.Valore}`}
				description='Effettua l’autolettura del tuo contatore idrico, accedi sullo Sportello OnLine'
				options={[
					'Apple',
					'Google',
					'Microsoft365',
					'MicrosoftTeams',
					'Outlook.com',
					'Yahoo',
				]}
				location='World Wide Web'
				startDate={utils.formatDateYearMonthDay(periodoLetturaStart)}
				endDate={utils.formatDateYearMonthDay(periodoLetturaEnd)}
				timeZone='Europe/Rome'
			/>
		)}
	</>
);

const MeterDetail = ({
	matricolaContatore,
	ultimaLettura,
	periodoLettura,
}: Props) => {
	const [showDetails, setShowDetails] = useState(false);

	const periodoLetturaStart = new Date(periodoLettura?.start);
	const periodoLetturaEnd = new Date(periodoLettura?.end);

	const validPeriodoLettura =
		utils.isValidDate(periodoLetturaStart) &&
		utils.isValidDate(periodoLetturaEnd);

	const onSOLClick = () => {
		agent.Tracking.track(
			`${Static.onSOLClick} ${Static.fromMeterDetailComponent}`,
			Static.urlVeritasSOL,
		);
	};

	const toggleDetails = () => {
		agent.Tracking.track(
			`${Static.toggleDetail} ${Static.fromMeterDetailComponent}`,
		);

		setShowDetails((prev) => !prev);
	};

	return (
		<>
			<div className='card app-card-with-background ps-5 pe-5 pt-3 pb-3 mb-3 d-block d-sm-none'>
				<button
					type='button'
					className='app-toggle-show app-collapse-button'
					onClick={toggleDetails}
					aria-expanded={showDetails}
					aria-controls='detail'
				>
					<h4 className='mb-0'>INFO LETTURA E CONTATORE</h4>
					<i
						className={showDetails ? 'fa fa-angle-up' : 'fa fa-angle-down'}
						style={{ fontSize: '1.5rem' }}
						aria-hidden='true'
					/>
				</button>
			</div>

			{showDetails && (
				<div
					id='detail'
					className='card app-card-with-background p-5 d-block d-sm-none mb-4'
				>
					<MeterDetailBody
						matricolaContatore={matricolaContatore}
						ultimaLettura={ultimaLettura}
						periodoLettura={periodoLettura}
						validPeriodoLettura={validPeriodoLettura}
						periodoLetturaStart={periodoLetturaStart}
						periodoLetturaEnd={periodoLetturaEnd}
						onSOLClick={onSOLClick}
					/>
				</div>
			)}

			<div className='card app-card-with-background p-5 d-none d-sm-block'>
				<h4>INFO LETTURA E CONTATORE</h4>
				<MeterDetailBody
					matricolaContatore={matricolaContatore}
					ultimaLettura={ultimaLettura}
					periodoLettura={periodoLettura}
					validPeriodoLettura={validPeriodoLettura}
					periodoLetturaStart={periodoLetturaStart}
					periodoLetturaEnd={periodoLetturaEnd}
					onSOLClick={onSOLClick}
					firstRowClassName='mt-4'
				/>
			</div>
		</>
	);
};

export default MeterDetail;
